/* 
    TODO:
    create multiple .js files, each representing a separated store,
    that will be imported into main store.js file and included into store object as module.
    https://vuex.vuejs.org/guide/modules.html
*/

import Vue from "vue";
import Vuex from "vuex";
import SharedMutations from "vuex-shared-mutations";
import VuexPersist from "vuex-persist";
import CartStore from "./cart";
import FiltersStore from "./filters";
import NavigationStore from "./navigation";
import DraftApi from "@/services/api/draftApi";

const DraftApiService = new DraftApi();

Vue.use(Vuex);

const getInitialPromoCodeState = () => ({
  promoCodeInvalid: false,
  promoCodeErrorMessage: null,
});

const state = {
  checkoutState: {
    cart: null,
    shipping: null,
    isPromoCodeLoading: false,
  },

  reorderState: null,
  packingSlipState: null,
  returnSlipState: null,
  userState: null,
  plusAccessState: null,
  keysState: {},
  notificationCount: 0,
  notificationSessionLoaded: false,
  promoCodeState: getInitialPromoCodeState(),
  favoriteState: [],
  draftUploadedStatus: {
    started: false,
    ended: false,
    error: false,
  },
  draftCount: 0,
  isRetailerApproved: undefined,
  userStatus: undefined,
  dropdown: "none",
};

const getters = {
  checkoutState: checkoutStateGetter,
  packingSlipState: packingSlipStateGetter,
  returnSlipState: returnSlipStateGetter,
  reorderState: reorderStateGetter,
  userState: userStateGetter,
  plusAccessState: plusAccessStateGetter,
  notificationState: notificationCountGetter,
  notificationSessionLoaded: notificationSessionLoadedGetter,
  promoCodeState: promoCodeStateGetter,
  favoritesState: favoritesStateGetter,
  draftStatus: draftStatus,
  draftCount: draftCountGetter,
  isRetailerApproved,
  getUserStatus,
};

const mutations = {
  setReorderObject,
  setCart,
  setFavoriteState,
  setCheckoutAddressInfo,
  removeCheckoutState,
  removeReorderState,
  setDataForPackingSlip,
  setDataForReturnSlip,
  removeCheckoutShipping,
  setUser,
  setPlusAccess,
  setKeysState,
  addKeysToKeysState,
  setPromoCodeLoadingState,
  setNotificationSessionLoaded,
  setNotificationCount,
  setPromoCodeInvalid,
  setPromoCodeErrorMessage,
  clearPromoCodeState,
  draftUploadStart,
  draftUploadEnded,
  draftUploadError,
  resetDraftError,
  resetDraftLoader,
  setRetailerApproved,
  setUserStatus,
  setDraftCount,
  setDropdownType,
};

const actions = {
  async getDraftCount(store) {
    const { commit, getters } = store;
    const role = getters.userState ? getters.userState.role : null;

    if (role === "BRAND") {
      const { data } = await DraftApiService.getDraftCount();
      commit("setDraftCount", data.count);
    }
  },
};

function setDropdownType(state, type) {
  state.dropdown = type;
}

function draftUploadStart(state) {
  state.draftUploadedStatus.started = true;
}
function draftUploadEnded(state) {
  state.draftUploadedStatus.ended = true;
}
function draftUploadError(state, message) {
  state.draftUploadedStatus.error = message;
}
function resetDraftError(state) {
  state.draftUploadedStatus.error = false;
}
function resetDraftLoader(state) {
  state.draftUploadedStatus = {
    started: false,
    ended: false,
    error: false,
  };
}

function draftStatus(state) {
  return state.draftUploadedStatus;
}

function draftCountGetter(state) {
  return state.draftCount;
}

function setDraftCount(state, count) {
  state.draftCount = count;
}

function isRetailerApproved(state) {
  return state.isRetailerApproved;
}

function getUserStatus(state) {
  return state.userStatus;
}

function setNotificationSessionLoaded(state, payload) {
  state.notificationSessionLoaded = payload;
}

function setNotificationCount(state, payload) {
  state.notificationCount = payload;
}

function setReorderObject(state, payload) {
  state.reorderState = payload;
}

function setDataForPackingSlip(state, payload) {
  state.packingSlipState = payload;
}

function setDataForReturnSlip(state, payload) {
  state.returnSlipState = payload;
}

function setCart(state, payload) {
  state.checkoutState.cart = payload;
}

function setCheckoutAddressInfo(state, payload) {
  state.checkoutState.shipping = payload;
}

function setPromoCodeLoadingState(state, payload) {
  state.checkoutState.isPromoCodeLoading = payload;
}

function setUser(state, payload) {
  state.userState = payload;
}

function setRetailerApproved(state, payload) {
  state.isRetailerApproved = payload;
}

function setUserStatus(state, payload) {
  state.userStatus = payload;
}

function setPlusAccess(state, payload) {
  state.plusAccessState = payload;
}

function setFavoriteState(state, payload) {
  state.favoriteState = payload;
}

function setKeysState(state, payload) {
  state.keysState = payload;
}

function addKeysToKeysState(state, payload) {
  state.keysState = {
    ...state.keysState,
    ...payload,
  };
}

function setPromoCodeInvalid(state, payload) {
  state.promoCodeState.promoCodeInvalid = payload;
}

function setPromoCodeErrorMessage(state, payload) {
  state.promoCodeState.promoCodeErrorMessage = payload;
}

function clearPromoCodeState(state) {
  state.promoCodeState = getInitialPromoCodeState();
}

function checkoutStateGetter(state) {
  return state.checkoutState;
}

function packingSlipStateGetter(state) {
  return state.packingSlipState;
}

function returnSlipStateGetter(state) {
  return state.returnSlipState;
}

function reorderStateGetter(state) {
  return state.reorderState;
}

function userStateGetter(state) {
  return state.userState;
}

function plusAccessStateGetter(state) {
  return state.plusAccessState;
}

function notificationCountGetter(state) {
  return state.notificationCount;
}
function notificationSessionLoadedGetter(state) {
  return state.notificationSessionLoaded;
}

function promoCodeStateGetter(state) {
  return state.promoCodeState;
}

function favoritesStateGetter(state) {
  return state.favoriteState;
}

function removeCheckoutState(state) {
  state.checkoutState = {
    cart: null,
    shipping: null,
  };
}

function removeCheckoutShipping(state) {
  state.checkoutState.shipping = null;
}

function removeReorderState(state) {
  state.reorderState = null;
}

const vuexLocalStorage = new VuexPersist({
  storage: window.localStorage,
  modules: ["checkoutState"],
});

export default new Vuex.Store({
  modules: {
    filters: FiltersStore,
    cart: CartStore,
    navigation: NavigationStore,
  },
  state,
  getters,
  mutations,
  actions,
  plugins: [
    vuexLocalStorage.plugin,
    SharedMutations({ predicate: ["setCart", "checkoutStateGetter", "setCheckoutAddressInfo"] }),
  ],
});
